import { generateArticle as localGenerateArticle, reviewArticle as localReviewArticle, rewriteSelection as localRewriteSelection } from "../ai/mockAiService.mjs";
import { articleToStreamText, chunkText } from "../ai/streamingArticle.mjs";
import { createProjectFromArticle, loadProjects, saveProjects, upsertProject } from "../projects/projectStore.mjs";

const defaultLocal = {
  loadProjects,
  saveProjects,
  generateArticle: localGenerateArticle,
  reviewArticle: localReviewArticle,
  rewriteSelection: localRewriteSelection
};

function getDefaultPreferences() {
  return {
    tone: "专业清晰",
    audience: "公众号普通读者",
    length: "medium",
    purpose: "解释说明",
    layoutTemplate: "fresh",
    brandDesc: "",
    expertiseAreas: "",
    toneGuidelines: "",
    audienceDesc: "",
    redLines: "",
    customPrompt: ""
  };
}

export function createWorkbenchApi({
  protocol = globalThis.location?.protocol || "http:",
  fetchImpl = globalThis.fetch?.bind(globalThis),
  local = defaultLocal
} = {}) {
  const directFileMode = protocol === "file:" || !fetchImpl;

  async function requestJson(path, options = {}) {
    const response = await fetchImpl(path, {
      credentials: "include",
      ...options,
      headers: {
        "content-type": "application/json",
        ...(options.headers || {})
      }
    });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.error || `Request failed with status ${response.status}`);
    }
    return payload;
  }

  return {
    directFileMode,

    async register(input) {
      if (directFileMode) return { user: { id: "local-user", email: input.email, displayName: input.displayName || input.email }, tokenAccount: { articlesLimit: 3, articlesUsed: 0, remainingArticles: 3, hasRemainingArticles: true } };
      return requestJson("/api/auth/register", {
        method: "POST",
        body: JSON.stringify(input)
      });
    },

    async login(input) {
      if (directFileMode) return { user: { id: "local-user", email: input.email, displayName: input.email }, tokenAccount: { articlesLimit: 99999, articlesUsed: 0, remainingArticles: 99999, hasRemainingArticles: true } };
      return requestJson("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(input)
      });
    },

    async getSession() {
      if (directFileMode) return { user: { id: "local-user", email: "local@example.com", displayName: "Local" }, tokenAccount: { articlesLimit: 0, articlesUsed: 0, remainingArticles: 99999, hasRemainingArticles: true } };
      return requestJson("/api/auth/me");
    },

    async logout() {
      if (directFileMode) return { ok: true };
      return requestJson("/api/auth/logout", { method: "POST" });
    },

    async getUserPreferences() {
      if (directFileMode) return getDefaultPreferences();
      return requestJson("/api/user/preferences");
    },

    async updateUserPreferences(prefs) {
      if (directFileMode) return { ...getDefaultPreferences(), ...prefs };
      return requestJson("/api/user/preferences", {
        method: "PUT",
        body: JSON.stringify(prefs)
      });
    },

    async getAccountInfo() {
      if (directFileMode) return { account: { articlesLimit: 0, articlesUsed: 0, remainingArticles: 0, hasRemainingArticles: true }, logs: [], redemptions: [] };
      return requestJson("/api/user/account");
    },

    async redeemCode(code) {
      if (directFileMode) return { account: { articlesLimit: 0, articlesUsed: 0, remainingArticles: 0 }, redemption: { articles: 0 } };
      return requestJson("/api/user/redeem", {
        method: "POST",
        body: JSON.stringify({ code })
      });
    },

    async listProjects() {
      if (directFileMode) return local.loadProjects();
      const payload = await requestJson("/api/projects");
      return payload.projects;
    },

    async createProject({ article, inputType }) {
      if (directFileMode) {
        const projects = local.loadProjects();
        const project = createProjectFromArticle(article, inputType);
        local.saveProjects(upsertProject(projects, project));
        return project;
      }
      const payload = await requestJson("/api/projects", {
        method: "POST",
        body: JSON.stringify({ article, inputType })
      });
      return payload.project;
    },

    async updateProject(id, patch) {
      if (directFileMode) {
        const projects = local.loadProjects();
        const current = projects.find((project) => project.id === id) || { id };
        const project = {
          ...current,
          ...patch,
          title: patch.title ?? current.title,
          article: patch.article ?? current.article,
          updatedAt: new Date().toISOString()
        };
        local.saveProjects(upsertProject(projects, project));
        return project;
      }
      const payload = await requestJson(`/api/projects/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch)
      });
      return payload.project;
    },

    async deleteProject(id) {
      if (directFileMode) {
        const projects = local.loadProjects();
        local.saveProjects(projects.filter((p) => p.id !== id));
        return { ok: true };
      }
      await requestJson(`/api/projects/${encodeURIComponent(id)}`, { method: "DELETE" });
      return { ok: true };
    },

    async generateArticle(input) {
      if (directFileMode) return local.generateArticle(input);
      return requestJson("/api/ai/generate-article", {
        method: "POST",
        body: JSON.stringify(input)
      });
    },

    async *generateArticleStream(input) {
      if (directFileMode) {
        const result = await local.generateArticle(input);
        for (const text of chunkText(articleToStreamText(result.article), 8)) {
          await delay(8);
          yield { type: "chunk", text };
        }
        yield { type: "article", article: result.article };
        return;
      }

      const response = await fetchImpl("/api/ai/generate-article-stream", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      });
      if (!response.ok) {
        throw new Error(await readStreamError(response));
      }
      yield* readNdjsonEvents(response);
    },

    async reviewArticle(article) {
      if (directFileMode) return local.reviewArticle(article);
      return requestJson("/api/ai/review-article", {
        method: "POST",
        body: JSON.stringify({ article })
      });
    },

    async rewriteSelection(input) {
      if (directFileMode) return local.rewriteSelection(input);
      return requestJson("/api/ai/rewrite-selection", {
        method: "POST",
        body: JSON.stringify(input)
      });
    }
  };
}

export const workbenchApi = createWorkbenchApi();

async function* readNdjsonEvents(response) {
  if (!response.body?.getReader) {
    throw new Error("生成接口没有返回可读取的流。");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() || "";

    for (const line of lines) {
      const event = parseNdjsonEvent(line);
      if (event) yield event;
    }
  }

  const tail = `${buffer}${decoder.decode()}`.trim();
  if (tail) {
    const event = parseNdjsonEvent(tail);
    if (event) yield event;
  }
}

function parseNdjsonEvent(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed) return null;
  const event = JSON.parse(trimmed);
  if (event.type === "error") throw new Error(event.error || "生成失败");
  return event;
}

async function readStreamError(response) {
  try {
    const payload = await response.json();
    return payload?.error || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
